import { useMemo } from 'react';
import { Wallet, Gift, Gamepad2, ArrowDownCircle, ArrowUpCircle, X } from 'lucide-react';
import { Button } from '../../../components/ui/button';

/**
 * Modal panel showing the player's full balance breakdown:
 * - Wallet (cash, USD)
 * - Freeplay credits (FP) — clickable, routes to Marketplace
 * - Total game balance
 * - Deposit / Cashout shortcuts
 */
export default function BalanceDetailPanel({ wallet, accounts, games, onPurchase, onCashout, onExchangeFP, close }) {
  const walletUsd = Number(wallet?.cash_balance ?? wallet?.balance ?? 0);
  const gameLines = useMemo(() => {
    return accounts.map(a => {
      const isCustom = a.is_custom || a.provider_code === 'manual';
      const game = isCustom
        ? { id: a.game_id, name: a.game_name || 'Custom Game' }
        : (games.find(g => g.id === a.game_id || g.code === a.game_code) || { name: a.game_name || 'Game' });
      return {
        id: a.id,
        name: game.name,
        balance: Number(a.last_known_balance ?? a.balance_usd ?? 0),
        custom: isCustom,
      };
    });
  }, [accounts, games]);
  const totalInGames = gameLines.reduce((s, g) => s + g.balance, 0);

  return (
    <div className="flex flex-col" data-testid="balance-detail-panel">
      <div className="px-5 pt-5 pb-4 flex items-center justify-between" style={{ borderBottom: '1px solid rgba(0,0,0,0.08)' }}>
        <div>
          <h3 className="display text-xl font-extrabold" style={{ color: '#0B0F0C' }}>Your Balances</h3>
          <p className="text-xs" style={{ color: '#6B7280' }}>Total across wallet & games</p>
        </div>
        <button onClick={close} className="w-9 h-9 rounded-full flex items-center justify-center" style={{ background: '#F3F4F6', color: '#0B0F0C' }}>
          <X className="w-5 h-5" />
        </button>
      </div>

      <div className="px-5 py-5 space-y-3">
        {/* Wallet balance */}
        <div className="rounded-2xl p-4 flex items-center gap-3" style={{ background: 'linear-gradient(135deg, #DCFCE7, #F0FDF4)', border: '1px solid rgba(34,197,94,0.25)' }} data-testid="balance-wallet-card">
          <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: 'radial-gradient(circle at 30% 30%, #B6FFD3, #22FF88 45%, #008F44 95%)', boxShadow: 'inset 0 0 10px rgba(255,255,255,0.4), 0 0 14px rgba(34,255,136,0.45)' }}>
            <Wallet className="w-6 h-6" style={{ color: '#02140A' }} />
          </div>
          <div className="flex-1">
            <p className="text-[10px] uppercase tracking-wider font-bold" style={{ color: '#15803D' }}>Wallet Balance</p>
            <p className="display text-2xl font-extrabold" style={{ color: '#0B0F0C' }}>${walletUsd.toFixed(2)}</p>
          </div>
        </div>

        {/* FP card */}
        <button
          type="button"
          onClick={onExchangeFP}
          className="w-full rounded-2xl p-4 flex items-center gap-3 transition active:scale-[0.99] hover:brightness-105 text-left"
          style={{
            background: 'linear-gradient(135deg, #FEF3C7, #FFFBEB)',
            border: '1px solid rgba(250,204,21,0.45)',
            boxShadow: '0 4px 14px rgba(250,204,21,0.18)',
          }}
          data-testid="balance-fp-card"
          aria-label="Open Marketplace to exchange Freeplay credits"
        >
          <div
            className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0"
            style={{
              background: 'radial-gradient(circle at 30% 30%, #FFF6B0, #FACC15 45%, #B45309 95%)',
              boxShadow: 'inset 0 0 10px rgba(255,255,255,0.4), 0 0 14px rgba(250,204,21,0.55)',
            }}
          >
            <Gift className="w-6 h-6" style={{ color: '#3F2A00' }} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-wider font-bold" style={{ color: '#92400E' }}>
              Freeplay Credits
            </p>
            <p className="display text-2xl font-extrabold" style={{ color: '#0B0F0C' }} data-testid="balance-fp-value">
              {Math.floor(Number(wallet?.play_credits ?? wallet?.fp_credits ?? 0)).toLocaleString()} <span className="text-base font-bold" style={{ color: '#92400E' }}>FP</span>
            </p>
            <p className="text-[11px] mt-0.5 font-bold" style={{ color: '#B45309' }}>
              Tap to exchange in Marketplace →
            </p>
          </div>
        </button>

        {/* Games total */}
        <div className="rounded-2xl p-4" style={{ background: 'linear-gradient(135deg, #EEF2FF, #F8FAFC)', border: '1px solid rgba(99,102,241,0.25)' }} data-testid="balance-games-card">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #6366F1, #4338CA)' }}>
              <Gamepad2 className="w-6 h-6" style={{ color: '#fff' }} />
            </div>
            <div className="flex-1">
              <p className="text-[10px] uppercase tracking-wider font-bold" style={{ color: '#4338CA' }}>Total in Games</p>
              <p className="display text-2xl font-extrabold" style={{ color: '#0B0F0C' }}>${totalInGames.toFixed(2)}</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2 pt-2">
          <Button
            onClick={onPurchase}
            className="balance-action-btn balance-action-deposit h-12 display font-bold rounded-xl"
            style={{
              background: 'linear-gradient(180deg,#28E879,#159447)',
              color: '#fff',
              border: '1px solid rgba(34,255,136,0.45)',
              boxShadow: '0 0 0 1px rgba(34,255,136,0.18), 0 10px 24px rgba(21,148,71,0.38), 0 0 18px rgba(34,255,136,0.42), inset 0 1px 0 rgba(255,255,255,0.26)',
            }}
            data-testid="balance-purchase"
          >
            <ArrowDownCircle className="balance-action-icon w-4 h-4 mr-1" /> Deposit
          </Button>
          <Button
            onClick={onCashout}
            className="balance-action-btn balance-action-cashout h-12 display font-bold rounded-xl"
            style={{
              background: 'linear-gradient(180deg,#FF7A1A,#D33A05)',
              color: '#fff',
              border: '1px solid rgba(249,115,22,0.48)',
              boxShadow: '0 0 0 1px rgba(249,115,22,0.18), 0 10px 24px rgba(194,65,12,0.38), 0 0 18px rgba(249,115,22,0.44), inset 0 1px 0 rgba(255,255,255,0.26)',
            }}
            data-testid="balance-cashout"
          >
            <ArrowUpCircle className="balance-action-icon w-4 h-4 mr-1" /> Cashout
          </Button>
        </div>
      </div>
    </div>
  );
}
